import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  ledgerChartCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { ledgerEndpoints } from "@/features/ledger/api/endpoints";
import type { ChartOfAccountDto } from "@/features/ledger/api/dto";

/**
 * Met à jour un compte comptable (`PUT /ChartOfAccounts/{id}`) — réservé à
 * flashpay-superadmin / flashpay-admin.
 */
export const useUpdateChartOfAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["ledger", "chart-of-accounts", "update"],
    mutationFn: ({
      chartOfAccountId,
      body,
    }: {
      chartOfAccountId: string;
      body: ChartOfAccountDto;
    }) =>
      mutateData({
        url: ledgerEndpoints.chartOfAccounts.update(chartOfAccountId),
        reqBody: body as unknown as Record<string, unknown>,
        method: "PUT",
      }) as Promise<ChartOfAccountDto>,
    onSuccess: (_data, { chartOfAccountId }) => {
      return invalidateAfterMutation(
        queryClient,
        ledgerChartCacheKeys(chartOfAccountId)
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useUpdateChartOfAccount failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "ledger" }
      );
    },
  });
};
